import type React from 'react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { useShellLayerScrollContext } from '../../../frame/Shell/utils';

export interface UseFieldErrorMessageOptions {
  /**
   * Scroll container to observe (defaults to window)
   */
  scrollContainer?: HTMLElement | null;
}

export interface UseFieldErrorMessageReturn {
  /**
   * Whether the error message is currently stuck
   */
  isStuck: boolean;
  /**
   * The position where the error message should stick ('top' or 'bottom')
   */
  stickyPosition: 'top' | 'bottom';
  /**
   * Ref to attach to the original error message element
   */
  originalRef: React.RefObject<HTMLDivElement | null>;
  /**
   * Ref to attach to the stuck error message element
   */
  stuckRef: React.RefObject<HTMLDivElement | null>;
  /**
   * Current z-index for the sticky element
   */
  zIndex: number;
}

export function useFieldErrorMessage({
  scrollContainer = null,
}: UseFieldErrorMessageOptions = {}): UseFieldErrorMessageReturn {
  const [isStuck, setIsStuck] = useState(false);
  const [stickyPosition, setStickyPosition] = useState<'top' | 'bottom'>('bottom');
  const [zIndex, setZIndex] = useState(10);
  const originalRef = useRef<HTMLDivElement>(null);
  const stuckRef = useRef<HTMLDivElement>(null);
  const elementId = useRef(`sticky-error-${Math.random().toString(36).substr(2, 9)}`);
  const lastStickyState = useRef<{ isStuck: boolean; position: 'top' | 'bottom' | null }>({
    isStuck: false,
    position: null,
  });

  const { layerManager } = useShellLayerScrollContext();

  // Get scroll container from context if none provided
  const contextScrollContainer = useShellLayerScrollContext().scrollContainer;
  const finalScrollContainer = scrollContainer || contextScrollContainer;

  // Safely unstick immediately for snappy feel
  const safeUnstick = useCallback(() => {
    setIsStuck(false);
    lastStickyState.current = { isStuck: false, position: null };
  }, []);

  // Calculate distance from scroll container edge
  const calculateDistance = useCallback(
    (position: 'top' | 'bottom'): number => {
      if (!originalRef.current) return 0;

      const rect = originalRef.current.getBoundingClientRect();
      const container = finalScrollContainer || window;
      const isWindow = container === window;

      if (isWindow) {
        // For window scroll, calculate relative to viewport
        if (position === 'top') {
          return Math.abs(rect.bottom);
        }
        return Math.abs(window.innerHeight - rect.top);
      }
      // For scroll container, calculate relative to container
      const containerRect = (container as HTMLElement).getBoundingClientRect();
      const relativeTop = rect.top - containerRect.top;
      const relativeBottom = rect.bottom - containerRect.top;
      const containerHeight = (container as HTMLElement).clientHeight;

      if (position === 'top') {
        // Distance from top of container (negative when above container)
        return Math.abs(relativeBottom);
      }
      // Distance from bottom of container (negative when below container)
      return Math.abs(containerHeight - relativeTop);
    },
    [finalScrollContainer],
  );

  // Update z-index when sticky state or position changes
  useEffect(() => {
    const currentElementId = elementId.current;

    if (isStuck) {
      const distance = calculateDistance(stickyPosition);
      const newZIndex = layerManager.registerElement(currentElementId, stickyPosition, distance);
      setZIndex(newZIndex);
    } else {
      layerManager.unregisterElement(currentElementId);
    }

    return () => {
      layerManager.unregisterElement(currentElementId);
    };
  }, [isStuck, stickyPosition, calculateDistance, layerManager]);

  // Update distance when position changes (but only if already stuck)
  useEffect(() => {
    const currentElementId = elementId.current;

    if (isStuck) {
      const distance = calculateDistance(stickyPosition);
      layerManager.updateElementDistance(currentElementId, distance);

      // Get the updated z-index after distance update
      const updatedZIndex = layerManager.getElementZIndex(currentElementId);
      if (updatedZIndex !== undefined) {
        setZIndex(updatedZIndex);
      }
    }

    return () => {
      layerManager.unregisterElement(currentElementId);
    };
  }, [isStuck, stickyPosition, calculateDistance, layerManager]);

  // Cleanup timeout on unmount
  useEffect(
    () => () => {
      // No timeout to clear here as we removed delays
    },
    [],
  );

  useLayoutEffect(() => {
    if (!originalRef.current) return undefined;

    const container = finalScrollContainer || window;
    const isWindow = container === window;
    const buffer = 5; // Buffer to prevent edge cases
    let scrollTimeout: NodeJS.Timeout | null = null;

    const handleScroll = () => {
      if (!originalRef.current) return;

      // Throttle scroll events to prevent excessive calls
      if (scrollTimeout) return;

      scrollTimeout = setTimeout(() => {
        scrollTimeout = null;
      }, 8); // ~120fps for more responsive sticky behavior

      // Get the element's position relative to the scroll container
      const rect = originalRef.current.getBoundingClientRect();

      // Always check if element is visible first - this prevents duplication during fast scrolling
      if (isWindow) {
        const viewportHeight = window.innerHeight;
        const isFullyVisible = rect.top >= 0 && rect.bottom <= viewportHeight;

        if (isFullyVisible && lastStickyState.current.isStuck) {
          safeUnstick();
          return;
        }
      } else {
        const containerRect = (container as HTMLElement).getBoundingClientRect();
        const relativeTop = rect.top - containerRect.top;
        const relativeBottom = rect.bottom - containerRect.top;
        const containerHeight = (container as HTMLElement).clientHeight;
        const isFullyVisible = relativeTop >= 0 && relativeBottom <= containerHeight;

        if (isFullyVisible && lastStickyState.current.isStuck) {
          safeUnstick();
          return;
        }
      }

      if (isWindow) {
        // For window scroll, use viewport dimensions
        const viewportHeight = window.innerHeight;

        // Check if element is fully visible in the viewport
        const isFullyVisible = rect.top >= 0 && rect.bottom <= viewportHeight;

        if (isFullyVisible) {
          // Element is fully visible - always unstick if currently stuck
          if (lastStickyState.current.isStuck) {
            safeUnstick();
          }
          return;
        }

        // Check if element is out of view (with small buffer)
        const isAboveViewport = rect.bottom < -buffer;
        const isBelowViewport = rect.top > viewportHeight + buffer;

        if (isAboveViewport || isBelowViewport) {
          // Determine sticky position based on element position
          let newStickyState = { isStuck: false, position: null as 'top' | 'bottom' | null };

          if (isAboveViewport) {
            // Element is above viewport - stick to top
            newStickyState = { isStuck: true, position: 'top' };
          } else if (isBelowViewport) {
            // Element is below viewport - stick to bottom
            newStickyState = { isStuck: true, position: 'bottom' };
          }

          // Only update state if there's a significant change
          if (
            newStickyState.isStuck !== lastStickyState.current.isStuck ||
            newStickyState.position !== lastStickyState.current.position
          ) {
            if (newStickyState.isStuck) {
              setStickyPosition(newStickyState.position!);
              setIsStuck(true);
              lastStickyState.current = newStickyState;
            } else {
              setIsStuck(false);
              lastStickyState.current = newStickyState;
            }
          }
        } else if (lastStickyState.current.isStuck) {
          // Element is partially visible - unstick if currently stuck
          safeUnstick();
        }
      } else {
        // For scroll container, calculate relative to container
        const containerRect = (container as HTMLElement).getBoundingClientRect();
        const relativeTop = rect.top - containerRect.top;
        const relativeBottom = rect.bottom - containerRect.top;
        const containerHeight = (container as HTMLElement).clientHeight;

        // Check if element is fully visible in the container
        const isFullyVisible = relativeTop >= 0 && relativeBottom <= containerHeight;

        if (isFullyVisible) {
          // Element is fully visible - always unstick if currently stuck
          if (lastStickyState.current.isStuck) {
            safeUnstick();
          }
          return;
        }

        // Check if element is out of view (with small buffer)
        const isAboveContainer = relativeBottom < -buffer;
        const isBelowContainer = relativeTop > containerHeight + buffer;

        if (isAboveContainer || isBelowContainer) {
          // Determine sticky position based on element position
          let newStickyState = { isStuck: false, position: null as 'top' | 'bottom' | null };

          if (isAboveContainer) {
            // Element is above container - stick to top
            newStickyState = { isStuck: true, position: 'top' };
          } else if (isBelowContainer) {
            // Element is below container - stick to bottom
            newStickyState = { isStuck: true, position: 'bottom' };
          }

          // Only update state if there's a significant change
          if (
            newStickyState.isStuck !== lastStickyState.current.isStuck ||
            newStickyState.position !== lastStickyState.current.position
          ) {
            if (newStickyState.isStuck) {
              setStickyPosition(newStickyState.position!);
              setIsStuck(true);
              lastStickyState.current = newStickyState;
            } else {
              setIsStuck(false);
              lastStickyState.current = newStickyState;
            }
          }
        } else if (lastStickyState.current.isStuck) {
          // Element is partially visible - unstick if currently stuck
          safeUnstick();
        }
      }
    };

    // Add scroll listener to handle sticky logic
    (container as HTMLElement).addEventListener('scroll', handleScroll, { passive: true });

    // Initial check
    handleScroll();

    return () => {
      (container as HTMLElement).removeEventListener('scroll', handleScroll);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, [finalScrollContainer, safeUnstick]);

  return {
    isStuck,
    stickyPosition,
    originalRef,
    stuckRef,
    zIndex,
  };
}
